/**
 * A record has two clocks, and the distance between them is not a fault.
 *
 * Every synced record in this system carries `occurredAt` — the **device's
 * clock**, when the terminal says it happened — and `recordedAt` — the
 * **server's clock**, when it reached the API and became part of a day's
 * takings. The API records the first and refuses to order anything by it
 * (FR-008, FR-009); it orders by the second.
 *
 * On a hill station the two can be **hours apart** and that is an ordinary
 * Tuesday: an outlet keeps selling through an outage and the queue drains when
 * the link returns. So a screen shows `occurredAt` as when the thing happened,
 * because that is the answer to "when did this customer pay", and names the gap
 * underneath rather than hiding it — the gap *is* the account of the outage, and
 * it is usually the only place on a screen where that account exists.
 *
 * It is never worded as a problem and never coloured like one. Nothing was lost;
 * a sale that took four hours to arrive is a sale the offline queue did its job
 * for.
 *
 * ## Why this is in `lib/` rather than beside a screen
 *
 * It used to live in `routes/tickets/ticket-time.ts`, whose own note said the
 * three lines of formatter were a third local copy and that **the fourth screen
 * to want them was the moment they moved here**. FE032's sales list is that
 * fourth screen, and it is in a different epic from Ticketing — reaching across
 * `routes/tickets/` for a formatter would couple two epics through a module
 * named for one of them, which is the coupling `src/sales/sales/business-day.ts`
 * refuses on the API side for the same reason (R-402).
 *
 * `routes/catalogue/instant.ts` is deliberately **not** folded in here. That one
 * owns a *parser* for a moment somebody types and the zod fragment that refuses
 * a bad one; this owns the read-only direction. They are different jobs and
 * merging them would produce a module that sometimes applies a zone and
 * sometimes must not.
 */

/**
 * The park's own clock and the park's own order — 13 Aug 2026, 21:50.
 *
 * The device's local zone, deliberately unset, matching `QueuedWrites` and
 * `catalogue/instant.ts`: whoever reads this is standing where the sale
 * happened, and an instant rendered in UTC has them subtracting five and a half
 * hours to work out which shift it belonged to.
 */
const WHEN = new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * `new Date(iso)` here parses a value the **server** sent. It reads no clock —
 * `lib/clock.ts` governs that, and nothing in this file asks what time it is now.
 */
export const formatInstant = (iso: string): string => WHEN.format(new Date(iso));

/**
 * How far apart the two clocks have to be before the difference is worth a
 * sentence.
 *
 * Every record has some gap: the request takes a moment and the two clocks are
 * never identical. Five minutes is comfortably above the ordinary case and
 * comfortably below anything a person would call an outage, so the line appears
 * when there is a story and stays quiet when there is not. A threshold of zero
 * would put a second line under every row in the table and teach everyone to
 * stop reading it, which is the same failure as a status column of one repeated
 * word.
 */
const WORTH_SAYING_MS = 5 * 60 * 1000;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** A span in the largest two units that describe it — `2h 14m`, `3d 4h`. */
function formatSpan(ms: number): string {
  if (ms >= DAY_MS) {
    const days = Math.floor(ms / DAY_MS);
    const hours = Math.floor((ms % DAY_MS) / HOUR_MS);
    return hours === 0 ? `${String(days)}d` : `${String(days)}d ${String(hours)}h`;
  }

  if (ms >= HOUR_MS) {
    const hours = Math.floor(ms / HOUR_MS);
    const minutes = Math.floor((ms % HOUR_MS) / MINUTE_MS);
    return minutes === 0 ? `${String(hours)}h` : `${String(hours)}h ${String(minutes)}m`;
  }

  return `${String(Math.floor(ms / MINUTE_MS))}m`;
}

/**
 * What happened between the device and the server, or `null` when nothing did.
 *
 * Two directions, because both occur and they say different things:
 *
 * - **The server accepted it later.** The ordinary case, and the account of an
 *   outage — the till kept selling and the queue drained afterwards.
 * - **The server accepted it earlier than the till says it happened.** The
 *   device's clock is ahead. Also not a fault and specifically not an
 *   impossibility: FR-008 exists precisely because a terminal's clock is not
 *   trusted, and this is what an untrusted clock looks like when you print it.
 *   Said plainly, because a row whose sale appears to happen after it was
 *   recorded otherwise reads as corrupted data.
 *
 * `Date.parse` reads no clock. A value the server could not have sent — anything
 * unparseable — answers `null` rather than `NaN` on a manager's screen.
 */
export function describeRecordingGap(occurredAt: string, recordedAt: string): string | null {
  const printed = Date.parse(occurredAt);
  const accepted = Date.parse(recordedAt);

  if (Number.isNaN(printed) || Number.isNaN(accepted)) return null;

  const gap = accepted - printed;

  if (Math.abs(gap) < WORTH_SAYING_MS) return null;

  return gap > 0
    ? `Reached the server ${formatSpan(gap)} later`
    : `The till's clock was ${formatSpan(-gap)} ahead`;
}
